'use client'

/**
 * TransportBookingClient — booking UI for activities with category === 'transport'.
 * Handles dual-segment (outbound + optional return), timezone display, extra fields,
 * and flat-rate pricing with per-extra-guest surcharge.
 *
 * When embedded={true}: hides breadcrumb/hero/title, replaces "Book & Pay" with
 * "Add to booking" which calls addServiceItem() and does NOT POST to any endpoint.
 */

import { useState, useCallback, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import type { Adventure } from '@/lib/data'
import type { Activity } from '@/lib/hooks/useActivities'
import type { ActivityExtraField, PricePerUnit } from '@/types/availability'
import { useClientTimezone } from '@/lib/hooks/useClientTimezone'
import { useUnifiedCart } from '@/lib/unified-cart'
import type { BookingSegment } from '@/lib/unified-cart'
import TimezoneSelector from '@/components/booking/activities/TimezoneSelector'
import ExtraFieldsForm from '@/components/booking/activities/ExtraFieldsForm'
import PersonSelector from '@/components/booking/activities/PersonSelector'
import SimpleDayCalendar from '@/components/booking/activities/SimpleDayCalendar'
import LodgeCrossSell from '@/components/booking/LodgeCrossSell'
import styles from './TransportBookingClient.module.css'

// Activity extended with transport-specific fields that are present at runtime
// but not yet in the Activity hook type. Uses optional fields for safety.
interface TransportActivity extends Activity {
  baseOccupancy?: number
  extraGuestPrice?: PricePerUnit
  requiresReturnDate?: boolean
  extraFields?: ActivityExtraField[]
  contactInfo?: string
}

interface TransportBookingClientProps {
  activity: Adventure
  firestoreActivity: TransportActivity
  embedded?: boolean
  prefillDates?: { checkIn?: string; checkOut?: string }
  prefillGuests?: number
  onAdded?: () => void
}

interface SegmentState {
  date: string
  time: string
}

function calculateTransportTotal(
  pricing: TransportActivity,
  outbound: SegmentState,
  returnSeg: SegmentState | null,
  guests: number,
): number {
  const baseOcc = pricing.baseOccupancy ?? 3
  const extraGuestRate = pricing.extraGuestPrice?.amount ?? 0
  const extraGuests = Math.max(0, guests - baseOcc)
  const extraFee = extraGuests * extraGuestRate

  const outboundBase =
    pricing.dailyPricing?.[outbound.date] ?? pricing.basePrice?.amount ?? 0
  const outboundTotal = outboundBase + extraFee

  if (!returnSeg || !returnSeg.date) return outboundTotal

  const returnBase =
    pricing.dailyPricing?.[returnSeg.date] ?? pricing.basePrice?.amount ?? 0
  const returnTotal = returnBase + extraFee

  return outboundTotal + returnTotal
}

export default function TransportBookingClient({
  activity,
  firestoreActivity,
  embedded = false,
  prefillDates,
  prefillGuests,
  onAdded,
}: TransportBookingClientProps) {
  const rawLocale = useLocale()
  const tQuickAdd = useTranslations('serviceQuickAdd')
  const locale = (['en', 'es', 'pt'].includes(rawLocale) ? rawLocale : 'en') as 'en' | 'es' | 'pt'

  const [clientTz, setClientTz] = useClientTimezone()
  const { addServiceItem } = useUnifiedCart()

  const [outbound, setOutbound] = useState<SegmentState>({ date: prefillDates?.checkIn ?? '', time: '' })
  const [returnSeg, setReturnSeg] = useState<SegmentState>({ date: prefillDates?.checkOut ?? '', time: '' })
  const [persons, setPersons] = useState(prefillGuests ?? 2)
  const [extraValues, setExtraValues] = useState<Record<string, string>>({})
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const requiresReturnDate = firestoreActivity.requiresReturnDate ?? false
  const extraFields = firestoreActivity.extraFields ?? []
  const dailyPricing = firestoreActivity.dailyPricing ?? {}
  const defaultPrice = firestoreActivity.basePrice?.amount ?? 0

  const total = useMemo(
    () =>
      calculateTransportTotal(
        firestoreActivity,
        outbound,
        requiresReturnDate ? returnSeg : null,
        persons,
      ),
    [firestoreActivity, outbound, returnSeg, persons, requiresReturnDate],
  )

  const handleExtraChange = useCallback((key: string, value: string) => {
    setExtraValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const validate = (): string[] => {
    const errors: string[] = []

    if (!outbound.date) errors.push('Please select an outbound date.')
    if (!outbound.time) errors.push('Please select an outbound time.')

    if (requiresReturnDate) {
      if (!returnSeg.date) errors.push('Please select a return date.')
      if (!returnSeg.time) errors.push('Please select a return time.')

      if (outbound.date && returnSeg.date) {
        if (returnSeg.date < outbound.date) {
          errors.push('Return date cannot be before the outbound date.')
        } else if (returnSeg.date === outbound.date && outbound.time && returnSeg.time) {
          if (returnSeg.time <= outbound.time) {
            errors.push('Return time must be after the outbound time on the same day.')
          }
        }
      }
    }

    for (const field of extraFields) {
      if (field.required && !extraValues[field.key]?.trim()) {
        const label = field.label[locale] ?? field.label.en
        errors.push(`"${label}" is required.`)
      }
    }

    if (persons < 1) errors.push('At least 1 person is required.')

    return errors
  }

  const buildSegments = (): BookingSegment[] => {
    const segs: BookingSegment[] = [
      {
        kind: 'outbound',
        date: outbound.date,
        time: outbound.time,
        tz: 'America/Santiago',
        priceUSD: calculateTransportTotal(firestoreActivity, outbound, null, persons),
      },
    ]
    if (requiresReturnDate && returnSeg.date) {
      segs.push({
        kind: 'return',
        date: returnSeg.date,
        time: returnSeg.time,
        tz: 'America/Santiago',
        priceUSD: calculateTransportTotal(firestoreActivity, returnSeg, null, persons),
      })
    }
    return segs
  }

  const handleAddToCart = () => {
    const errors = validate()
    setValidationErrors(errors)
    if (errors.length > 0) return

    const segments = buildSegments()
    addServiceItem({
      itemType: 'service',
      activityId: activity.id,
      activitySlug: activity.id,
      activityName: activity.title,
      activityImage: activity.heroImage ?? '',
      segments,
      persons,
      extraFieldValues: extraValues,
      clientTimezone: clientTz,
      subtotalUSD: total,
    })

    onAdded?.()
  }

  const handleSubmit = async () => {
    const errors = validate()
    setValidationErrors(errors)
    if (errors.length > 0) return

    setSubmitting(true)
    try {
      const segments = buildSegments()

      const payload = {
        activityId: activity.id,
        activityName: activity.title,
        segments,
        guests: persons,
        extraFieldValues: extraValues,
        clientTimezone: clientTz,
        totalAmount: total,
      }

      const res = await fetch('/api/bookings/transport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const data = (await res.json()) as { error?: string }
        throw new Error(data.error ?? 'Booking request failed')
      }

      const { bookingId } = (await res.json()) as { bookingId: string }
      window.location.href = `/checkout?bookingId=${bookingId}`
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred. Please try again.'
      setValidationErrors([msg])
    } finally {
      setSubmitting(false)
    }
  }

  const isFormReady =
    !!outbound.date &&
    !!outbound.time &&
    (!requiresReturnDate || (!!returnSeg.date && !!returnSeg.time)) &&
    persons >= 1 &&
    extraFields.filter((f) => f.required).every((f) => !!extraValues[f.key]?.trim())

  return (
    <section className={styles.bookingSection}>
      <div className={styles.container}>
        <div className={styles.layout}>

          {/* Main booking panel */}
          <div className={styles.mainPanel}>

            {/* Timezone selector */}
            <div className={styles.tzRow}>
              <TimezoneSelector value={clientTz} onChange={setClientTz} />
            </div>

            {/* Outbound segment */}
            <div className={styles.segmentBlock}>
              <h3 className={styles.segmentTitle}>
                {requiresReturnDate ? '🛫 Outbound / Ida' : '📅 Select Date'}
              </h3>

              <SimpleDayCalendar
                activityId={activity.id}
                selectedDate={outbound.date}
                onSelectDate={(date) => setOutbound((prev) => ({ ...prev, date }))}
                maxMonths={6}
                defaultPricePerPerson={defaultPrice}
                blockedDates={[]}
                dailyPricing={dailyPricing}
              />

              <div className={styles.slotRow}>
                <h4 className={styles.slotTitle}>Select time</h4>
                <input
                  type="time"
                  className={styles.timeInput}
                  value={outbound.time}
                  onChange={(e) => setOutbound((prev) => ({ ...prev, time: e.target.value }))}
                  aria-label="Outbound pickup time"
                />
              </div>
            </div>

            {/* Return segment */}
            {requiresReturnDate && (
              <div className={styles.segmentBlock}>
                <h3 className={styles.segmentTitle}>🛬 Return / Vuelta</h3>

                <SimpleDayCalendar
                  activityId={activity.id}
                  selectedDate={returnSeg.date}
                  onSelectDate={(date) => setReturnSeg((prev) => ({ ...prev, date }))}
                  maxMonths={6}
                  defaultPricePerPerson={defaultPrice}
                  blockedDates={[]}
                  dailyPricing={dailyPricing}
                  minDate={outbound.date ? new Date(outbound.date + 'T00:00:00') : undefined}
                />

                <div className={styles.slotRow}>
                  <h4 className={styles.slotTitle}>Select return time</h4>
                  <input
                    type="time"
                    className={styles.timeInput}
                    value={returnSeg.time}
                    onChange={(e) => setReturnSeg((prev) => ({ ...prev, time: e.target.value }))}
                    aria-label="Return pickup time"
                  />
                </div>
              </div>
            )}

            {/* Extra fields */}
            {extraFields.length > 0 && (
              <div className={styles.extraFieldsBlock}>
                <h3 className={styles.segmentTitle}>Additional information</h3>
                <ExtraFieldsForm
                  fields={extraFields}
                  values={extraValues}
                  onChange={handleExtraChange}
                  locale={locale}
                />
              </div>
            )}
          </div>

          {/* Right panel — person selector + price + pay */}
          <div className={styles.rightPanel}>
            <div className={styles.stickyContainer}>
              <div className={styles.panelCard}>
                <PersonSelector
                  persons={persons}
                  minPersons={1}
                  maxPersons={50}
                  pricePerPerson={defaultPrice}
                  onUpdate={setPersons}
                  showPricing={false}
                />

                {/* Price breakdown */}
                <div className={styles.priceBlock}>
                  <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>Total</span>
                    <span className={styles.priceValue}>${total.toLocaleString()} USD</span>
                  </div>
                  {firestoreActivity.baseOccupancy && persons > firestoreActivity.baseOccupancy && (
                    <p className={styles.priceMeta}>
                      Includes extra passenger surcharge
                    </p>
                  )}
                  {requiresReturnDate && (
                    <p className={styles.priceMeta}>Round-trip pricing</p>
                  )}
                </div>

                {/* Validation errors */}
                {validationErrors.length > 0 && (
                  <ul className={styles.errors}>
                    {validationErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}

                {/* Submit */}
                {embedded ? (
                  <button
                    className={styles.payButton}
                    onClick={handleAddToCart}
                    disabled={!isFormReady}
                  >
                    {tQuickAdd('saveCta')}
                  </button>
                ) : (
                  <button
                    className={styles.payButton}
                    onClick={handleSubmit}
                    disabled={!isFormReady || submitting}
                  >
                    {submitting ? 'Processing…' : 'Book & Pay'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Lodge cross-sell — hidden in embedded mode */}
          {!embedded && (
            <div className={styles.crossSellPanel}>
              <LodgeCrossSell
                activityId={activity.id}
                locale={locale}
                className={styles.lodgeCrossSell}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
